import React, { useMemo } from 'react';
import Link from 'next/link';
import classNames from 'classnames/bind';
import { usePCWideMediaQuery, useTabletPCMediaQuery } from '../../hooks/useResponsiveMediaQuery';
import PostThumbnail from '../post/PostThumbnail';
import Carousel from '../common/Carousel';
import { StoredPost } from '../../types/post';
import { getPostContentSrc } from '../../utils/postInfo';
import { getPostPageUrl } from '../../utils/url';
import { chunkArray } from '../../utils/array';
import nClicks from '../../utils/nClicks';
import styles from './RelatedPosts.module.scss';

const cx = classNames.bind(styles);

function requestNClicks(e: React.MouseEvent) {
  nClicks(e, 'post.relatedposts');
}

const RelatedPostWrapper: React.ComponentType<{ className: string; isPcWide: boolean; isTabletPC: boolean }> = ({
  children,
  className,
  isPcWide,
  isTabletPC,
}) => {
  // - PC: <ul>에 4개의 post 단순 나열
  if (isPcWide) {
    return <ul className={className}>{children}</ul>;
  }

  // - Tablet: <Carousel>에 페이지 당 2개의 post가 보이도록 나열
  // - Mobile: <Carousel>에 페이지 당 1개의 post가 보이도록 나열
  const id = isTabletPC ? 'related-posts-tablet' : 'related-posts-mobile';
  return (
    <Carousel id={id} key={id} className={className}>
      {children}
    </Carousel>
  );
};

export type RelatedPostsProps = {
  relatedPosts: StoredPost[];
};

function RelatedPosts({ relatedPosts }: RelatedPostsProps): JSX.Element {
  // 반응형
  const isPcWide = usePCWideMediaQuery();
  const isTabletPC = useTabletPCMediaQuery();

  // carousel
  const chunkedPostsMap = useMemo(
    () => ({
      1: chunkArray(relatedPosts, 1), // for PC, Mobile:  [ [post1], [post2], [post3], [post4], ... ]
      2: chunkArray(relatedPosts, 2), // for Tablet:      [ [post1, post2], [post3, post4], ... ]
    }),
    [relatedPosts]
  );
  const chunkedPosts = isTabletPC ? chunkedPostsMap[2] : chunkedPostsMap[1];

  return (
    <RelatedPostWrapper className={cx('related_posts', '-expand')} isPcWide={isPcWide} isTabletPC={isTabletPC}>
      {chunkedPosts.map((chunk) => {
        const key = chunk?.length === 1 ? chunk[0].id : chunk.map((post) => post.id ?? '').join('-');

        return (
          <li key={key} className={cx('related_posts_item_wrapper')}>
            {chunk.map((post) => {
              const url = getPostPageUrl(post.year, post.month, post.slug);
              const heroImageSrc = post.heroImage ? getPostContentSrc(post.heroImage) : undefined; // heroImage가 없을 경우 에러 이미지 표시
              const heroVideoSrc = post.heroVideo ? getPostContentSrc(post.heroVideo) : undefined;

              return (
                <div key={post.id} className={cx('related_posts_item')}>
                  <Link href={url}>
                    <a className={cx('related_post_link')} onClick={requestNClicks}>
                      <PostThumbnail
                        className={cx('related_post_img')}
                        isScheduled={post.isScheduled}
                        thumbUrl={heroImageSrc}
                        videoUrl={heroVideoSrc}
                      />
                      <strong className={cx('related_post_title')}>{post.title}</strong>
                    </a>
                  </Link>
                </div>
              );
            })}
          </li>
        );
      })}
    </RelatedPostWrapper>
  );
}

export default RelatedPosts;
